import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
} from '@mui/material';
import {
  TrendingUp,
  People,
  AttachMoney,
  Devices,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { dashboardAPI } from '../../services/api';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import numeral from 'numeral';

const StatCard = ({ title, value, icon, color = 'primary', subtitle, trend }) => (
  <Card>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography color="text.secondary" gutterBottom variant="overline">
            {title}
          </Typography>
          <Typography variant="h4" component="div">
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
          {trend && (
            <Box sx={{ mt: 1 }}>
              <Chip
                label={`${trend > 0 ? '+' : ''}${trend}%`}
                color={trend > 0 ? 'success' : 'error'}
                size="small"
                icon={<TrendingUp />}
              />
            </Box>
          )}
        </Box>
        <Box
          sx={{
            backgroundColor: `${color}.light`,
            borderRadius: 2,
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const DashboardContent = () => {
  const { data: summaryData, isLoading: summaryLoading } = useQuery(
    'dashboard-summary',
    () => dashboardAPI.getSummary(),
    { refetchInterval: 5 * 60 * 1000 }
  );

  const { data: realtimeData, isLoading: realtimeLoading } = useQuery(
    'dashboard-realtime',
    () => dashboardAPI.getRealtime(),
    { refetchInterval: 90 * 1000 }
  );

  if (summaryLoading || realtimeLoading) {
    return <LoadingSpinner message="Loading dashboard..." />;
  }

  const summary = summaryData?.data?.summary || {};
  const subscriptions = summaryData?.data?.subscriptions || {};
  // topCountries available for future use: summaryData?.data?.topCountries || []
  const realtime = realtimeData?.data || {};

  return (
    <Box>
      {/* Key Metrics */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            title="Total Installations"
            value={numeral(summary.totalInstallations).format('0,0')}
            icon={<Devices color="primary" />}
            color="primary"
            subtitle="All time"
            trend={summary.growthRate}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            title="New Installations"
            value={numeral(summary.newInstallations).format('0,0')}
            icon={<People color="success" />}
            color="success"
            subtitle="Last 30 days"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            title="Active Users"
            value={numeral(summary.activeUsers).format('0,0')}
            icon={<TrendingUp color="info" />}
            color="info"
            subtitle="Last 7 days"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            title="Revenue"
            value={numeral(summary.revenue).format('$0,0')}
            icon={<AttachMoney color="warning" />}
            color="warning"
            subtitle="Last 30 days"
          />
        </Grid>
      </Grid>

      {/* Real-time Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Today's Activity</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="h4" color="primary">
                    {numeral(realtime.today?.activeUsers).format('0,0')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Active Users</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h4" color="secondary">
                    {numeral(realtime.today?.sessions).format('0,0')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Sessions</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h4" color="success.main">
                    {numeral(realtime.today?.duration / 60).format('0,0')}h
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Usage Time</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h4" color="error.main">
                    {numeral(realtime.today?.errors).format('0,0')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Errors</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Subscription Distribution</Typography>
              <Box sx={{ mt: 2 }}>
                {['free', 'standard', 'professional', 'enterprise'].map((tier) => (
                  <Box key={tier} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{tier}</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {numeral(subscriptions[tier]).format('0,0')}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardContent;

